/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Mail, Send, RefreshCw, Settings, Bell, AlertTriangle, CheckCircle2,
  XCircle, Clock, FlaskConical, RotateCcw, Filter, Search, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section } from "@/components/AnalyticsCommon";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

const TYPE_LABEL = {
  etapa_onboarding_vencida: "Etapa de onboarding vencida",
  lembrete_30_60_90: "Lembrete 30/60/90 dias",
  pdi_vencido: "PDI com ação vencida",
  avaliacao_pendente: "Avaliação pendente",
  treinamento_recomendado: "Treinamento recomendado",
  dossie_gerado: "Dossiê gerado",
};

const STATUS_CFG = {
  sent: { label: "Enviado", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  failed: { label: "Falhou", color: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle },
  skipped: { label: "Não enviado", color: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle },
  duplicate: { label: "Duplicado", color: "bg-slate-50 text-slate-700 border-slate-200", icon: Clock },
  disabled: { label: "Desativado", color: "bg-slate-50 text-slate-600 border-slate-200", icon: ShieldAlert },
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "master" || user?.role === "rh_admin";

  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [checking, setChecking] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, hist, tpl] = await Promise.all([
        isAdmin ? api.get("/notifications/email/config").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        api.get("/notifications/email/history", { params: { days: 30 } }),
        api.get("/notifications/email/templates"),
      ]);
      setConfig(cfg.data);
      setHistory(hist.data || []);
      setTemplates(tpl.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = history;
    if (filterType) arr = arr.filter((h) => h.notif_type === filterType);
    if (filterStatus) arr = arr.filter((h) => h.status === filterStatus);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((h) =>
        (h.employee_name || "").toLowerCase().includes(q) ||
        (h.to_email || "").toLowerCase().includes(q) ||
        (h.subject || "").toLowerCase().includes(q),
      );
    }
    return arr;
  }, [history, filterType, filterStatus, search]);

  const kpis = useMemo(() => {
    const sent = history.filter((h) => h.status === "sent").length;
    const failed = history.filter((h) => h.status === "failed").length;
    const skipped = history.filter((h) => h.status === "skipped").length;
    const dup = history.filter((h) => h.status === "duplicate").length;
    return { sent, failed, skipped, dup, total: history.length };
  }, [history]);

  const runCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/notifications/email/check-due", null,
        { params: { dry_run: false } });
      toast.success(`${data.sent} enviadas · ${data.detected} detectadas · ${data.skipped} ignoradas.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setChecking(false); }
  };

  const resend = async (id) => {
    try {
      const { data } = await api.post(`/notifications/email/resend/${id}`);
      toast.success(`Reenvio: ${data.status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5 pb-10" data-testid="notifications-page">
      <PageHero
        kicker={<><Bell className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Comunicação</>}
        title="Notificações & E-mails"
        description="Alertas automáticos para etapas vencidas de onboarding, avaliações 30/60/90, PDI vencido e treinamentos recomendados — entregues via Resend."
        actions={
          isAdmin && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setTestOpen(true)}
                      className="h-10" data-testid="notif-test-btn">
                <FlaskConical className="h-3.5 w-3.5 mr-1" /> Teste de envio
              </Button>
              <Button size="sm" onClick={runCheck} disabled={checking}
                      className="h-10 bg-[#234935] hover:bg-[#2c5c43] text-white"
                      data-testid="notif-check-due-btn">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${checking ? "animate-spin" : ""}`} />
                {checking ? "Verificando..." : "Verificar pendências"}
              </Button>
            </div>
          )
        }
      />

      {config && !config.resend_configured && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
             data-testid="notif-no-key-warning">
          <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-[12.5px] text-amber-900">
            <b>RESEND_API_KEY não configurada.</b> Os envios serão registrados no histórico como
            "Não enviado" até que a chave seja adicionada na variável de ambiente <code className="bg-amber-200/40 px-1 rounded">RESEND_API_KEY</code> no backend.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="notif-kpis">
        <KpiCard label="Total (30 dias)" value={kpis.total} icon={Bell} accent="emerald" testId="notif-total" />
        <KpiCard label="Enviadas" value={kpis.sent} icon={CheckCircle2} accent="emerald" testId="notif-sent" />
        <KpiCard label="Não enviadas" value={kpis.skipped} icon={AlertTriangle} accent="ochre" testId="notif-skipped" />
        <KpiCard label="Falhas" value={kpis.failed} icon={XCircle} accent="rose" testId="notif-failed" />
        <KpiCard label="Duplicadas evitadas" value={kpis.dup} icon={Clock} accent="slate" testId="notif-dup" />
      </div>

      <Tabs defaultValue="history" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="history" data-testid="notif-tab-history">Histórico</TabsTrigger>
          <TabsTrigger value="templates" data-testid="notif-tab-templates">Templates</TabsTrigger>
          {isAdmin && <TabsTrigger value="config" data-testid="notif-tab-config">Configuração</TabsTrigger>}
        </TabsList>

        <TabsContent value="history" className="mt-4">
          <Section kicker="Logs" title="Histórico de envios" testId="notif-history-list">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..."
                       className="pl-9 h-9" data-testid="notif-search" />
              </div>
              <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="notif-filter-type"><SelectValue placeholder="Todos os tipos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Todos os status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  {Object.entries(STATUS_CFG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {loading ? <Skeleton className="h-[300px]" /> : filtered.length === 0 ? (
              <div className="text-center py-12 text-[13px] text-muted-foreground">
                <Mail className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                Nenhuma notificação no período.
              </div>
            ) : (
              <div className="space-y-1.5">
                {filtered.map((h) => {
                  const cfg = STATUS_CFG[h.status] || STATUS_CFG.skipped;
                  const Icon = cfg.icon;
                  return (
                    <div key={h.id} className="rounded-lg border border-border bg-white p-3"
                         data-testid={`notif-row-${h.id}`}>
                      <div className="flex items-start gap-2">
                        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${
                          h.status === "sent" ? "text-emerald-600" :
                          h.status === "failed" ? "text-rose-600" :
                          h.status === "skipped" ? "text-amber-600" : "text-slate-500"
                        }`} />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-[13px] truncate">{h.subject}</div>
                          <div className="text-[10.5px] text-muted-foreground flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[9px]">{TYPE_LABEL[h.notif_type] || h.notif_type}</Badge>
                            <span>{h.to_email}</span>
                            {h.employee_name && <span>· {h.employee_name}</span>}
                            <span>· {fmtDate(h.sent_at)}</span>
                          </div>
                          {h.error && (
                            <div className="mt-1 text-[10.5px] text-rose-700 italic">⚠ {h.error}</div>
                          )}
                        </div>
                        <Badge variant="outline" className={`text-[9px] capitalize ${cfg.color}`}>{cfg.label}</Badge>
                        {isAdmin && (h.status === "failed" || h.status === "skipped") && (
                          <Button size="icon" variant="ghost" onClick={() => resend(h.id)}
                                  data-testid={`notif-resend-${h.id}`} title="Reenviar">
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <Section kicker="Catálogo" title="Templates de e-mail">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map((t) => (
                <div key={t.key} className="rounded-lg border border-border bg-white p-3"
                     data-testid={`notif-tpl-${t.key}`}>
                  <div className="font-display font-semibold text-[13.5px] text-[#234935]">{t.title}</div>
                  <p className="text-[11.5px] text-muted-foreground mt-1">{t.description}</p>
                  <div className="mt-2">
                    <Badge variant="outline" className={`text-[9px] ${
                      config?.types_enabled?.[t.key] ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                     : "bg-slate-100 text-slate-600"
                    }`}>
                      {config?.types_enabled?.[t.key] ? "Ativo" : "Desativado"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="config" className="mt-4">
            <ConfigPanel config={config} onSaved={load} />
          </TabsContent>
        )}
      </Tabs>

      <TestSendDialog open={testOpen} onClose={() => setTestOpen(false)} templates={templates} onSent={load} />
    </div>
  );
}

function ConfigPanel({ config, onSaved }) {
  const [form, setForm] = useState({
    rh_email: config?.rh_email || "",
    diretoria_email: config?.diretoria_email || "",
    fixed_email: config?.fixed_email || "",
    days_before_reminder: config?.days_before_reminder ?? 3,
    check_frequency_hours: config?.check_frequency_hours ?? 24,
    signature_html: config?.signature_html || "",
    types_enabled: { ...(config?.types_enabled || {}) },
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/notifications/email/config", form);
      toast.success("Configuração salva.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Section kicker="Admin" title="Configuração de notificações"
             description="A API Key do Resend é configurada via variável de ambiente do backend (não exposta no frontend).">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <Label className="text-[11px] uppercase tracking-wider">E-mail RH (destinatário padrão)</Label>
          <Input value={form.rh_email} onChange={(e) => setForm({ ...form, rh_email: e.target.value })}
                 type="email" data-testid="notif-cfg-rh" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider">E-mail diretoria</Label>
          <Input value={form.diretoria_email} onChange={(e) => setForm({ ...form, diretoria_email: e.target.value })}
                 type="email" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider">E-mail fixo extra</Label>
          <Input value={form.fixed_email} onChange={(e) => setForm({ ...form, fixed_email: e.target.value })}
                 type="email" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider">Dias antes do prazo (lembrete)</Label>
          <Input type="number" min={0} max={30} value={form.days_before_reminder}
                 onChange={(e) => setForm({ ...form, days_before_reminder: Number(e.target.value) })} />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider">Frequência de check (horas)</Label>
          <Input type="number" min={1} max={168} value={form.check_frequency_hours}
                 onChange={(e) => setForm({ ...form, check_frequency_hours: Number(e.target.value) })} />
        </div>
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider mb-2 block">Tipos ativos</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(TYPE_LABEL).map(([k, v]) => (
            <label key={k} className="flex items-center gap-2 rounded-lg border border-border bg-white p-2.5 text-[12.5px]">
              <Switch checked={!!form.types_enabled[k]}
                      onCheckedChange={(c) => setForm({ ...form, types_enabled: { ...form.types_enabled, [k]: c } })}
                      data-testid={`notif-cfg-type-${k}`} />
              <span>{v}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-[11px] uppercase tracking-wider">Assinatura institucional (HTML)</Label>
        <Textarea rows={3} value={form.signature_html}
                  onChange={(e) => setForm({ ...form, signature_html: e.target.value })}
                  placeholder="<p>TheoKratos RH · Atenciosamente.</p>" />
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving}
                className="bg-[#234935] hover:bg-[#2c5c43] text-white" data-testid="notif-cfg-save">
          {saving ? "Salvando..." : "Salvar configuração"}
        </Button>
      </div>
    </Section>
  );
}

function TestSendDialog({ open, onClose, templates, onSent }) {
  const [toEmail, setToEmail] = useState("");
  const [tplKey, setTplKey] = useState("avaliacao_pendente");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!toEmail) { toast.error("Informe um e-mail"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/notifications/email/test",
        { to_email: toEmail, template_key: tplKey });
      toast.success(`Status: ${data.status}${data.error ? " — " + data.error : ""}`);
      onClose && onClose();
      onSent && onSent();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent aria-describedby="t-d" data-testid="notif-test-dialog">
        <DialogHeader><DialogTitle>Teste de envio de e-mail</DialogTitle></DialogHeader>
        <p id="t-d" className="sr-only">Envia um e-mail de exemplo para validar o template selecionado.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Destinatário</Label>
            <Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                   placeholder="seu@email.com" data-testid="notif-test-email" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Template</Label>
            <Select value={tplKey} onValueChange={setTplKey}>
              <SelectTrigger data-testid="notif-test-tpl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={sending}
                  className="bg-[#234935] hover:bg-[#2c5c43] text-white" data-testid="notif-test-send">
            <Send className="h-3.5 w-3.5 mr-1.5" />
            {sending ? "Enviando..." : "Enviar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
